import React from "react";
import { Link } from "react-router-dom";
import { getTimeAgo } from "../../utils/timeAgo";
import {
  Calendar,
  Clock,
  Heart,
  MessageSquare,
  ArrowRight,
  Tag,
} from "lucide-react";

export interface BlogCardProps {
  id?: number | string;
  title: string;
  description?: string;
  body?: string;
  pub_date?: string;
  image?: string;
  image_url?: string;
  category?: string | { name: string; id?: number };
  slug?: string;
  total_likes?: number;
  total_comments?: number;
  className?: string;
  isWrappedInLink?: boolean;
}

const DEFAULT_BLOG_IMAGE =
  "https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=800&q=80";

const calculateReadTime = (text?: string): string => {
  if (!text) return "3 min read";
  const words = text.replace(/<[^>]*>?/gm, "").trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
};

const BlogCard: React.FC<BlogCardProps> = ({
  id,
  title,
  description,
  body,
  pub_date,
  image,
  image_url,
  category,
  slug,
  total_likes = 0,
  total_comments = 0,
  className = "",
  isWrappedInLink = false,
}) => {
  const rawSnippet = description || body || "";
  const contentSnippet =
    typeof rawSnippet === "string"
      ? rawSnippet.replace(/<[^>]*>?/gm, "").trim()
      : "";

  const coverImage = image_url || image || DEFAULT_BLOG_IMAGE;

  const categoryName =
    typeof category === "string"
      ? category
      : category && typeof category === "object"
      ? category.name
      : "Tech Insights";

  const targetLink = slug && !isWrappedInLink ? `/blog/${slug}` : undefined;

  const CardInner = (
    <article
      className={`cht-group cht-relative cht-flex cht-flex-col cht-h-full cht-w-full cht-bg-white cht-rounded-[20px] cht-border cht-border-slate-200/80 cht-shadow-soft hover:cht-shadow-[0_22px_45px_rgba(62,87,218,0.12)] hover:cht-border-[#3e57da]/40 hover:-cht-translate-y-1.5 cht-transition-all cht-duration-300 cht-overflow-hidden ${className}`}
    >
      {/* 16:9 Media Showcase Container */}
      <div className="cht-relative cht-aspect-[16/9] cht-w-full cht-overflow-hidden cht-bg-slate-100">
        <img
          src={coverImage}
          alt={title}
          loading="lazy"
          className="cht-w-full cht-h-full cht-object-cover group-hover:cht-scale-105 cht-transition-transform cht-duration-500"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = DEFAULT_BLOG_IMAGE;
          }}
        />

        {/* Ambient Dark Vignette Gradient */}
        <div className="cht-absolute cht-inset-0 cht-bg-gradient-to-t cht-from-black/60 cht-via-transparent cht-to-black/10" />

        {/* Apple HIG Frosted Glass Category Pill (Top Left) */}
        {categoryName && (
          <div className="cht-absolute cht-top-3.5 cht-left-3.5">
            <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1 cht-bg-white/90 cht-backdrop-blur-md cht-border cht-border-white/40 cht-rounded-full cht-text-[11px] cht-font-black cht-text-[#3e57da] cht-uppercase cht-tracking-wider cht-shadow-sm">
              <Tag size={11} className="cht-text-[#3e57da]" />
              {categoryName}
            </span>
          </div>
        )}

        {/* Read Time Pill (Top Right) */}
        <div className="cht-absolute cht-top-3.5 cht-right-3.5">
          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-bg-slate-900/75 cht-backdrop-blur-md cht-border cht-border-white/20 cht-rounded-full cht-text-[10px] cht-font-bold cht-text-white cht-shadow-sm">
            <Clock size={11} className="cht-text-blue-300" />
            {calculateReadTime(rawSnippet)}
          </span>
        </div>
      </div>

      {/* Card Content Body */}
      <div className="cht-p-5 sm:cht-p-6 cht-flex cht-flex-col cht-flex-1 cht-space-y-3">
        {/* Publication Metadata Row */}
        {pub_date && (
          <div className="cht-flex cht-items-center cht-gap-2 cht-text-xs cht-font-semibold cht-text-slate-400">
            <Calendar size={13} className="cht-text-[#3e57da]" />
            <span>{getTimeAgo(pub_date)}</span>
          </div>
        )}

        {/* Article Title */}
        <h4 className="!cht-font-heading cht-text-lg sm:cht-text-xl !cht-font-extrabold cht-text-[#1e293b] group-hover:cht-text-[#3e57da] cht-transition-colors cht-line-clamp-2 !cht-leading-snug">
          {title}
        </h4>

        {/* Excerpt Snippet */}
        <p className="cht-text-slate-600 cht-text-sm !cht-leading-relaxed cht-line-clamp-3 cht-flex-1">
          {contentSnippet ||
            "Explore technical deep dives, architectural breakdowns, and modern engineering practices from Codehub Technologies."}
        </p>

        {/* Card Engagement & Action Footer */}
        <div className="cht-pt-3.5 cht-border-t cht-border-slate-100 cht-flex cht-items-center cht-justify-between cht-mt-auto">
          {/* Social / Engagement Stats */}
          <div className="cht-flex cht-items-center cht-gap-3.5">
            <span
              className="cht-inline-flex cht-items-center cht-gap-1.5 cht-text-xs cht-font-semibold cht-text-slate-500"
              title="Likes"
            >
              <Heart
                size={13}
                className={total_likes > 0 ? "cht-text-rose-500 cht-fill-rose-500" : "cht-text-slate-400"}
              />
              <span>{total_likes}</span>
            </span>

            <span
              className="cht-inline-flex cht-items-center cht-gap-1.5 cht-text-xs cht-font-semibold cht-text-slate-500"
              title="Comments"
            >
              <MessageSquare size={13} className="cht-text-indigo-500" />
              <span>{total_comments}</span>
            </span>
          </div>

          {/* Interactive Read Action */}
          <span className="cht-text-xs cht-font-bold cht-text-[#3e57da] cht-inline-flex cht-items-center cht-gap-1.5 group-hover:cht-text-[#2d41a8] cht-transition-colors">
            Read Article
            <ArrowRight
              size={14}
              className="group-hover:cht-translate-x-1.5 cht-transition-transform cht-duration-300"
            />
          </span>
        </div>
      </div>
    </article>
  );

  if (targetLink) {
    return (
      <Link to={targetLink} className="cht-block cht-h-full cht-w-full cht-no-underline">
        {CardInner}
      </Link>
    );
  }

  return CardInner;
};

export default BlogCard;
